import { memo } from 'react';
import { format } from 'date-fns';
import TableContainer from '@mui/material/TableContainer';
import { Typography } from '@components/Typography';
import { Table } from '@components/Table';
import { useTranslation, useTableSort } from '@hooks';
import { analyticsService } from '@services';
import ExportCSV from '@features/Analytics/ExportCSV';
import { PaperStyled, TableStyled } from '../../Feedback/FeedbackTable';
import { getTableBody } from './SocialPostsTableBody';
import { getCollapsibleTableBody } from './CollapsibleRowBody';

const SORT_COLUMNS = {
    title: 'campaignTitle',
    reach: 'reach',
    likes: 'likes',
    comments: 'comments',
    shares: 'shares',
    clicks: 'clicks',
    saves: 'saves',
};

const GROUP_BY = { key: row => row.campaignId, isParent: row => !!row.campaignTotal };

const getTableHeader = t => {
    return [
        {
            key: 0,
            width: '5%',
            component: <></>,
        },
        {
            key: 1,
            width: '25%',
            sortKey: 'title',
            align: 'left',
            component: (
                <Typography variant="subtitle2" align="left">
                    {t('common.title')}
                </Typography>
            ),
        },
        {
            key: 3,
            width: '10%',
            sortKey: 'reach',
            align: 'right',
            component: (
                <Typography align="right" variant="subtitle2">
                    {t('AnalyticsSocialPosts.reach')}
                </Typography>
            ),
        },
        {
            key: 4,
            width: '10%',
            sortKey: 'likes',
            align: 'right',
            component: (
                <Typography align="right" variant="subtitle2">
                    {t('AnalyticsSocialPosts.likes')}
                </Typography>
            ),
        },
        {
            key: 5,
            width: '10%',
            sortKey: 'comments',
            align: 'right',
            component: (
                <Typography align="right" variant="subtitle2">
                    {t('AnalyticsSocialPosts.comments')}
                </Typography>
            ),
        },
        {
            key: 6,
            width: '10%',
            sortKey: 'shares',
            align: 'right',
            component: (
                <Typography align="right" variant="subtitle2">
                    {t('AnalyticsSocialPosts.shares')}
                </Typography>
            ),
        },
        {
            key: 7,
            width: '10%',
            sortKey: 'clicks',
            align: 'right',
            component: (
                <Typography align="right" variant="subtitle2">
                    {t('AnalyticsSocialPosts.clicks')}
                </Typography>
            ),
        },
        {
            key: 8,
            width: '10%',
            sortKey: 'saves',
            align: 'right',
            component: (
                <Typography align="right" variant="subtitle2">
                    {t('AnalyticsSocialPosts.saves')}
                </Typography>
            ),
        },
    ];
};

const CollapsibleRow = ({ data, index }) => {
    return <Table rows={getCollapsibleTableBody(data, index)} />;
};

const SocialPostsTable = memo(({ isLoading, data, filter }) => {
    const { t } = useTranslation();
    const { sort, onSortChange, sortedData } = useTableSort(data, { columns: SORT_COLUMNS, groupBy: GROUP_BY });

    const onExport = exportFormat =>
        analyticsService.exportAnalyticsCSV(
            {
                ...filter,
                locations: filter.locations.map(item => item.id),
                from: filter.timeframe.start ? format(new Date(filter.timeframe.start), 'yyyy-MM-dd') : null,
                to: filter.timeframe.end ? format(new Date(filter.timeframe.end), 'yyyy-MM-dd') : null,
            },
            'social-posts',
            null,
            exportFormat,
        );
    return (
        <PaperStyled elevation={1}>
            <div
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}
            >
                <Typography variant="title1" sx={{ alignSelf: 'center' }}>
                    {t(`Header.socialPosts`)}
                </Typography>
                <ExportCSV type="social-posts" apiCall={onExport} />
            </div>
            <TableContainer>
                <TableStyled
                    data={sortedData}
                    columnNames={getTableHeader(t)}
                    rows={getTableBody(sortedData)}
                    isDataLoading={isLoading}
                    isCollapsible={true}
                    CollapsibleRow={CollapsibleRow}
                    sort={sort}
                    onSortChange={onSortChange}
                    getRowKey={row => row.campaignId}
                />
            </TableContainer>
        </PaperStyled>
    );
});

SocialPostsTable.displayName = 'SocialPostsTable';

export default SocialPostsTable;
